import React, { useState, useContext, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  TableSortLabel, 
  Autocomplete, 
  TextField, 
  Box,
  Typography,
  CircularProgress 
} from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContextProvider';
import { Link } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import { getTZDateTimeAsString } from '../utils/DateUtils';
import * as Constants from '../Constants'; 
import '../App.css';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('lastPingTime');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const params = {
          _site: user._site._id,
        };
        await rpc({
          method: "GetUsers",
          params,
          setErrorMessage,
          setOpenErrorDialog,
          onSuccess: setUsers
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [user]);

  const onCreate = () => {
    navigate('/createUser');
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const getRoles = (user) => {
    return user.roles ? user.roles.join(', ') : '';
  };

  const getGroups = (user) => {
    return user._eventGroups ? user._eventGroups.map((group) => group.displayName).join(', ') : '';
  };

  const getCareFor = (user) => {
    return user._careGiverFor ? user._careGiverFor.map((cgf) => `${cgf.first} ${cgf.last}`).join(', ') : '';
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAutocompleteSelect = (event, selectedUser) => {
    if (selectedUser && selectedUser._id) {
      navigate(`/user/${selectedUser._id}`);
    }
  };

  const sortedUsers = users.slice().sort((a, b) => {
    if (orderBy === 'user') {
      return order === 'asc'
        ? (a.email || "").localeCompare(b.email || "")
        : (b.email || "").localeCompare(a.email || "");
    } else if (orderBy === 'name') {
      const nameA = `${a.first} ${a.last}`;
      const nameB = `${b.first} ${b.last}`;
      return order === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    } else if (orderBy === 'lastPingTime') {
      const timeA = a.lastPingTime ? new Date(a.lastPingTime).getTime() : 0;
      const timeB = b.lastPingTime ? new Date(b.lastPingTime).getTime() : 0;
      return order === 'asc' ? timeA - timeB : timeB - timeA;
    }
    return 0;
  });

  return (
    <div className="UserList" style={{ padding: '20px' }}>
      {/* Search and Actions Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3, 
        gap: 2,
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>Find User:</Typography>
          <Autocomplete
            disablePortal
            id="user-search-nav"
            options={users}
            // Logic to search by Name or Email
            getOptionLabel={(option) => `${option.first} ${option.last} (${option.email})`}
            sx={{ width: 400, backgroundColor: 'white' }}
            onChange={handleAutocompleteSelect}
            renderInput={(params) => (
              <TextField {...params} label="Search users..." variant="outlined" size="small" />
            )}
          />
        </Box>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={onCreate}
          sx={{ height: '40px' }}
        >
          Create User
        </Button>
      </Box>

      {/* Main Table or Loading Spinner */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer className="TableContainer" component={Paper}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'user'}
                    direction={orderBy === 'user' ? order : 'asc'}
                    onClick={() => handleSort('user')}
                  >
                    <b>User</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    <b>Name</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell><b>Phone</b></TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'lastPingTime'}
                    direction={orderBy === 'lastPingTime' ? order : 'asc'}
                    onClick={() => handleSort('lastPingTime')}
                  >
                    <b>Last Ping</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell><b>Volume</b></TableCell>
                <TableCell><b>Version</b></TableCell>
                <TableCell><b>Roles</b></TableCell>
                <TableCell><b>Groups</b></TableCell>
                <TableCell><b>Caring For</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((u) => (
                <TableRow key={u._id}>
                  <TableCell>
                    <Link to={`/user/${u._id}`}>{u.email}</Link>
                  </TableCell>
                  <TableCell>{u.first} {u.last}</TableCell>
                  <TableCell>{u.phone}</TableCell>
                  <TableCell>
                    {u.lastPingTime ? getTZDateTimeAsString(u.lastPingTime, u._site.timezone) : ''}
                  </TableCell>
                  <TableCell>{u.volume || Constants.DEFAULT_VOLUME}</TableCell>
                  <TableCell>{u.clientVersion || ''}</TableCell>
                  <TableCell>{getRoles(u)}</TableCell>
                  <TableCell>{getGroups(u)}</TableCell>
                  <TableCell>{getCareFor(u)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ErrorDialog 
        open={openErrorDialog} 
        onErrorDialogClose={handleClose} 
        errorMessage={errorMessage} 
      />
    </div>
  );
};

export default UserList;